import Image from 'next/image'
import { Fragment } from 'react'
import { Icon } from '@captaincause/ui/Icon'
import { tm } from '@captaincause/ui/tm'
import { flattenImage } from 'utils/strapi/image'
import { CTABanner } from 'components/BlockCTABanner'
import { StrapiImage } from 'components/StapiImage'
import type { CTABannerProps } from 'components/BlockCTABanner'
import type { LocalImage, StrapiApiImage } from 'types/strapi/image'

type Quote = {
  author: string
  image: StrapiApiImage | LocalImage
  logo: StrapiApiImage | LocalImage
  title: string
}

export interface QuotesProps {
  title: string | null
  quotes: Quote[] | null
  banner?: CTABannerProps | null
}

export default function Quotes({
  title,
  quotes,
  banner = undefined,
}: QuotesProps) {
  return (
    <div className="h-auto overflow-hidden bg-white">
      <div className="relative mx-auto max-w-[1400px] px-4 py-12 lg:px-8 lg:py-20">
        {title ? (
          <h2 className="relative z-[1] text-center font-default text-4xl font-semibold">
            {title}
          </h2>
        ) : null}
        {quotes ? (
          <div
            className={tm(
              'relative z-[1] mx-auto grid w-fit grid-cols-1 gap-y-6 pt-10 lg:grid-cols-1 lg:grid-rows-[repeat(2,_minmax(0,_auto))] lg:gap-x-2 lg:gap-y-8',
              quotes.length === 2 && 'lg:grid-cols-[auto_auto]',
              quotes.length === 3 && 'lg:grid-cols-[auto_auto_auto]',
              banner && 'pb-10',
            )}
          >
            {quotes.map((quote, index) => {
              const avatar =
                'data' in quote.image ? flattenImage(quote.image) : quote.image
              const logo =
                'data' in quote.logo ? flattenImage(quote.logo) : quote.logo

              const [author, job] = (quote.author ?? '').split(', ')

              return (
                <Fragment key={quote.author ?? quote.title}>
                  <div
                    className="flex h-fit max-w-[450px] flex-col items-center gap-y-2 lg:row-start-1"
                    key={quote.author ?? quote.title}
                  >
                    <Icon icon="quote" />

                    {quote.title ? (
                      <div className="text-md px-4 text-left lg:px-8 lg:pl-16">
                        {quote.title}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex max-w-[450px] flex-col items-center lg:row-start-2">
                    {avatar ? (
                      <div className="relative mb-2 h-fit w-fit">
                        {'url' in avatar ? (
                          <StrapiImage
                            {...avatar}
                            className="h-20 w-20 rounded-full"
                          />
                        ) : (
                          <Image
                            src={avatar.image}
                            height={72}
                            alt={avatar.alt}
                            className="h-[128px] w-[128px] rounded-full object-cover"
                          />
                        )}
                      </div>
                    ) : null}
                    {author ? (
                      <div className="text-center font-medium">{author}</div>
                    ) : null}
                    {job ? (
                      <div className="mb-3 max-w-[150px] text-center text-sm text-dark-gray">
                        {job}
                      </div>
                    ) : null}
                    {logo ? (
                      <div className="flex flex-1 items-end justify-center">
                        {'url' in logo ? (
                          <StrapiImage {...logo} className="h-12 w-fit" />
                        ) : (
                          <Image
                            src={logo.image}
                            height={48}
                            alt={logo.alt}
                            className="h-12 w-fit"
                          />
                        )}
                      </div>
                    ) : null}
                  </div>
                </Fragment>
              )
            })}
          </div>
        ) : null}
        {banner ? <CTABanner {...banner} /> : null}
      </div>
    </div>
  )
}
